import React from 'react';
import classNames from 'classnames/bind';
import Button from 'components/App/Button';

import NotAllowedIcon from 'assets/images/not-allowed-icon.svg';
import TimeIcon from 'assets/images/time-icon.svg';

import { OPTION_CANCEL_PENALTY, themeColor } from 'constants/index';
import PropTypes from 'prop-types';
import formatDate from 'utils/formatDate';

import styles from '../CancellationPolicy.module.scss';

const cx = classNames.bind(styles);

function CancellationPolicyItem(props) {
  const { policy, onEdit, onApply, onDelete } = props;

  const descriptionCancelPolicy = React.useMemo(() => {
    const { freeBeforeDays, cancelPenalty } = policy;

    switch (true) {
      case freeBeforeDays === 0 && cancelPenalty === 'FIRST_NIGHT':
        return [
          'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng.',
          'Khách sẽ phải trả giá của đêm đầu tiên nếu hủy phòng trước khi tới nhận phòng.',
        ];
      case freeBeforeDays === 0 && cancelPenalty === '50_PERCENT':
        return [
          'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng.',
          `Khách sẽ phải trả ${OPTION_CANCEL_PENALTY[cancelPenalty].label} nếu hủy phòng trước khi tới nhận phòng.`,
        ];
      case freeBeforeDays === 0 && cancelPenalty === '100_PERCENT':
        return [
          'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng.',
          'Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trước khi tới nhận phòng.',
        ];
      case freeBeforeDays !== 0 && cancelPenalty === 'FIRST_NIGHT':
        return [
          `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
          `Khách sẽ phải trả giá của đêm đầu tiên nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
        ];
      case freeBeforeDays !== 0 && cancelPenalty === '50_PERCENT':
        return [
          `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
          `Khách sẽ phải trả ${OPTION_CANCEL_PENALTY[cancelPenalty].label} nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
        ];
      case freeBeforeDays !== 0 && cancelPenalty === '100_PERCENT':
        return [
          `Khách có thể hủy miễn phí đến ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
          `Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trong vòng ${freeBeforeDays} ngày trước khi tới nhận phòng.`,
        ];
      default:
        return [
          'Khách không thể hủy đặt phòng miễn phí trước khi tới nhận phòng.',
          'Khách sẽ phải trả toàn bộ tiền phòng nếu hủy phòng trước khi tới nhận phòng.',
        ];
    }
  }, [policy]);

  return (
    <div className={cx('CancellationPolicyItem')}>
      <div className={cx('CancellationPolicyItem__Header')}>
        <img
          src={policy.freeBeforeDays > 0 ? TimeIcon : NotAllowedIcon}
          alt="CancellationPolicyItem"
        />

        <div className={cx('Header__Content')}>
          <div className={cx('Header__Content__Description')}>
            <div className={cx('Header__Content__Description__Title')}>
              <p>
                {policy.freeBeforeDays > 0
                  ? `Linh động - ${policy.freeBeforeDays} ngày`
                  : 'Không hoàn hủy'}
              </p>
              {policy.isApplied && (
                <div className={cx('Description__Button')}>Đang áp dụng</div>
              )}
            </div>

            <p className={cx('Header__Content__Description__Subtitle')}>
              Giai đoạn áp dụng:
              {' '}
              <span>
                {formatDate(policy.startAt)}
                {' '}
                -
                {' '}
                {formatDate(policy.endAt)}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className={cx('CancellationPolicyItem__Body')}>
        <ul>
          {descriptionCancelPolicy.map((description) => (
            <li
              className={cx(policy.isApplied ? 'is-applied' : 'is-not-applied')}
              key={description}
            >
              {description}
            </li>
          ))}
          <li
            className={cx(policy.isApplied ? 'is-applied' : 'is-not-applied')}
          >
            {policy.absentPenalty === 'EQUAL_CANCEL_PENALTY'
              ? 'Nếu khách không đến, họ sẽ phải trả phí bằng với phí huỷ đặt phòng.'
              : 'Nếu khách không đến, họ sẽ phải trả toàn bộ tiền phòng.'}
          </li>
        </ul>
      </div>

      <div className={cx('CancellationPolicyItem__Footer')}>
        {!policy.isApplied && (
          <Button
            color={themeColor.LINK}
            classes={cx('Button', 'Button__Delete')}
            onClick={() => onDelete(policy)}
          >
            Xoá
          </Button>
        )}

        <Button
          color={themeColor.LINK}
          classes={cx('Button', 'Button__Edit')}
          onClick={() => onEdit(policy)}
        >
          Chỉnh sửa
        </Button>

        {!policy.isApplied && (
          <Button
            color={themeColor.SECONDARY}
            classes={cx('Button', 'Button__Apply')}
            onClick={() => onApply(policy)}
          >
            Áp dụng
          </Button>
        )}
      </div>
    </div>
  );
}

CancellationPolicyItem.propTypes = {
  policy: PropTypes.instanceOf(Object).isRequired,
  onEdit: PropTypes.func,
  onApply: PropTypes.func,
  onDelete: PropTypes.func,
};

CancellationPolicyItem.defaultProps = {
  onEdit: () => {},
  onApply: () => {},
  onDelete: () => {},
};

export default CancellationPolicyItem;
